import bindAllOfThis, { bindAllOfThisTheHardWay } from "src/utils/BindThisHelper";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { Trash, TrashConfig } from "../objects/Trash";
import { PhaserGamePad } from "src/games/shared/Gamepad";
import TranslateApi from "src/utils/TranslateHelper";
import { PhaserButtonConfig } from "src/games/shared/Button";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "./EndGameScene";
import { timingSafeEqual } from "crypto";
export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    gamePad: PhaserGamePad;
    xVel: number = 0;
    yVel: number = 0;
    speed: number;
    diver: Phaser.Physics.Arcade.Sprite;
    boat: Phaser.Physics.Arcade.Sprite;
    trash: Phaser.Physics.Arcade.Group;
    currentTrash: Trash;
    bubbles: Phaser.GameObjects.Sprite[];
    snugglefish: Phaser.Physics.Arcade.Sprite;
    fishy: Phaser.GameObjects.Sprite;
    counter: number;
    counterText: Phaser.GameObjects.Text;

    NORMAL_SPEED: number = 200;
    TURBO_SPEED: number = this.NORMAL_SPEED + 100;

    constructor() {
        super({
            key: "MainScene"
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload() {
        this.load.spritesheet("background", require("src/assets/qci/sprites/gameOceanCleanup/background.png"), { frameWidth: 1360, frameHeight: 768 });
        this.load.spritesheet("foreground", require("src/assets/qci/sprites/gameOceanCleanup/foreground.png"), { frameWidth: 1360, frameHeight: 768 });
        this.load.tilemapTiledJSON("map", require("src/assets/qci/maps/ocean_cleanup.json"));
        this.load.spritesheet('diver', require("src/assets/qci/sprites/gameOceanCleanup/diver.png"), { frameWidth: 40, frameHeight: 80 });
        this.load.spritesheet('boat', require("src/assets/qci/sprites/gameOceanCleanup/submarine.png"), { frameWidth: 900, frameHeight: 170 });
        this.load.spritesheet('fishy', require("src/assets/qci/sprites/gameOceanCleanup/fishy.png"), { frameWidth: 375, frameHeight: 300 });
        this.load.spritesheet('snugglefish', require("src/assets/qci/sprites/gameOceanCleanup/snugglefish.png"), { frameWidth: 250, frameHeight: 150 });
        this.load.spritesheet('bubbles', require("src/assets/qci/sprites/gameOceanCleanup/bubbles.png"), { frameWidth: 15, frameHeight: 60 });

        this.load.spritesheet("immobile-trash", require("src/assets/qci/sprites/gameOceanCleanup/immobile_assets.png"), { frameWidth: 100, frameHeight: 100 });

        for (var spritesheet of TrashConfig.TRASH) {
            this.load.spritesheet(spritesheet.name, spritesheet.src, { frameWidth: 70, frameHeight: 70 });
        }

        PhaserGamePad.preload(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        this.trash = this.physics.add.group();
        this.currentTrash = null;
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_a", this.speedBoost);
        this.speed = this.NORMAL_SPEED;
        this.bubbles = [];
        this.counter = 0;
    }

    create() {
        this.initialize();
        this.createScene();
        this.createCollisions();
        this.createBubbles();
        this.createCounter();
    }

    createDiver() {
        this.diver = this.physics.add.sprite(this.gameWidth / 2, 250, "diver", 4);
        this.diver.setSize(40, 75);
        this.diver.setDepth(2.6);
        this.diver.setCollideWorldBounds(true);

        this.diver.body.allowRotation = true;
        this.anims.create({
            key: "swim",
            frames: this.anims.generateFrameNumbers("diver", { start: 0, end: 1 }),
            frameRate: 4,
            repeat: -1,
        });
        this.diver.anims.play('swim');
    }

    /**
     * Pressing A gives a turbo boost for 1/2 second
     */
    speedBoost() {
        if (this.speed > this.NORMAL_SPEED) {
            return;
        }

        this.speed = this.TURBO_SPEED;

        setTimeout(() => {
            this.speed = this.NORMAL_SPEED;
        }, 500);
    }

    createScene() {
        var background = this.add.sprite(0, 0, "background", 0);
        background.setOrigin(0);
        background.setDepth(0);
        var foreground = this.add.sprite(0, 0, "foreground", 0);
        foreground.setOrigin(0);
        foreground.setDepth(3);

        this.anims.create({
            key: "background",
            frames: this.anims.generateFrameNumbers("background", { start: 0, end: 1 }),
            frameRate: 1,
            repeat: -1
        });

        background.anims.play('background');

        this.anims.create({
            key: "foreground",
            frames: this.anims.generateFrameNumbers("foreground", { start: 0, end: 1 }),
            frameRate: 1,
            repeat: -1
        });

        foreground.anims.play('foreground');

        this.createDiver();

        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "boat") {
                this.boat = this.physics.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "boat", 0);
                this.boat.setSize(850, 100);
                this.boat.setOffset(25, 60);

                this.anims.create({
                    key: "bob",
                    frames: this.anims.generateFrameNumbers("boat", { start: 0, end: 3 }),
                    frameRate: 1,
                    repeat: -1,
                });
                this.boat.anims.play('bob');

                // If diver is carrying trash and overlaps with the boat AND B is pressed, put trash on the boat
                this.physics.add.overlap(this.diver, this.boat, this.putTrashOnBoat, this.canPutTrashOnBoat);
            }
            else if (ob.type === "trash") {
                var config = TrashConfig.TRASH.find((t) => t.name === ob.name);
                if (config == null) {
                    config = TrashConfig.IMMOBILE_TRASH.find((t) => t.name === ob.name);
                }

                var pieceOfTrash = new Trash(this, ob.x + ob.width / 2, ob.y + ob.height / 2, config);
                if (config.name === "cans" || config.name === "bottle") {
                    pieceOfTrash.setSize(50, 50);
                }
                if (config.animates) {
                    this.createTrashAnimation(config, pieceOfTrash);
                }
                this.trash.add(pieceOfTrash);
            }
            else if (ob.name === "fishy") {
                this.fishy = this.add.sprite(ob.x, ob.y, "fishy", 0);
                this.fishy.setOrigin(0);
                this.fishy.setDepth(2.5);
                this.anims.create({
                    key: 'fishy',
                    frames: this.anims.generateFrameNumbers("fishy", { start: 1, end: 3 }),
                    frameRate: 3,
                    repeat: -1
                });
                this.fishy.anims.play('fishy');

                this.anims.create({
                    key: 'openMouth',
                    frames: this.anims.generateFrameNumbers("fishy", { start: 1, end: 4 }),
                    frameRate: 8,
                });
            }
            else if (ob.name === "snugglefish") {
                this.snugglefish = this.physics.add.sprite(ob.x, ob.y, "snugglefish", 0);
                this.snugglefish.setOrigin(0);
                this.snugglefish.setDepth(2);
                this.anims.create({
                    key: 'snugglefish',
                    frames: this.anims.generateFrameNumbers("snugglefish", { start: 0, end: 3 }),
                    frameRate: 3,
                    repeat: -1
                });
                this.snugglefish.anims.play('snugglefish');
            }
            else if (ob.name === "nearSnuggleFish") {
                var snuggleFishZone = this.add.zone(ob.x, ob.y, ob.width, ob.height);
                snuggleFishZone.setOrigin(0);
                this.physics.add.existing(snuggleFishZone, true);
                this.physics.add.overlap(this.diver, snuggleFishZone, this.hideSnugglefish);
            }
            else if (ob.name === "nearFishy") {
                var fishyZone = this.add.zone(ob.x, ob.y, ob.width, ob.height);
                fishyZone.setOrigin(0);
                this.physics.add.existing(fishyZone, true);
                this.physics.add.overlap(this.diver, fishyZone, this.openFishyMouth);
            }
        });

        // If the diver isn't carrying any trash and overlaps with a piece of trash that isn't already on the boat, pick up that trash
        this.physics.add.overlap(this.diver, this.trash, this.pickUpTrash, this.canPickUpTrash);
    }

    createCollisions() {
        this.map.getObjectLayer("Collision").objects.forEach((ob: TiledGameObject) => {
            var zone = this.add.zone(ob.x, ob.y, ob.width, ob.height);
            zone.setOrigin(0);
            this.physics.add.existing(zone, true);
            this.physics.add.collider(this.diver, zone);
        });
    }

    createBubbles() {
        this.map.getObjectLayer("Bubbles").objects.forEach((ob: TiledGameObject) => {
            var bubbles = this.add.sprite(ob.x, ob.y, "bubbles", 0);
            bubbles.setOrigin(0);
            bubbles.setName(ob.name);
            this.anims.create({
                key: ob.name,
                frames: this.anims.generateFrameNumbers("bubbles", { start: 0, end: 3 }),
                frameRate: 2,
                repeat: -1
            });
            bubbles.anims.play(ob.name);
            this.bubbles.push(bubbles);
        });
    }

    createCounter() {
        this.counter = this.trash.getChildren().length;
        var style = { fontFamily: "Russo One, Arial", fontSize: 35, color: "#000000" };
        this.counterText = this.add.text(this.gameWidth - 50, 20, this.counter + "", style);
    }

    isCarryingTrash() {
        return this.currentTrash != null;
    }

    canPutTrashOnBoat(diver, boat) {
        return this.isCarryingTrash();
    }

    /**
     * Only pick up trash if you're not already carrying some and it's not already on the boat
     */
    canPickUpTrash(diver, trash) {
        return !this.isCarryingTrash() && !trash.onBoat;
    }

    /**
     * Diver picks up trash
     */
    pickUpTrash(diver, trash: Trash) {
        trash.depth = diver.depth;
        this.currentTrash = trash;
        // Destroy the animations
        trash.anims.stop();
        var anim = this.anims.get(trash.name + "_bubbles");
        if (anim != null) {
            anim.destroy();
        }
        // Destroy the bubbles if it has them
        var bubbles = this.bubbles.find((b) => b.name === trash.name + "_bubbles");
        if (bubbles != null) {
            bubbles.destroy();
        }

        // When you pick up something other than the cans, it'll resume fishy's normal animation
        if(trash.name !== "cans") {
            this.fishy.anims.play("fishy", true);
        }
    }

    createTrashAnimation(config: TrashConfig, trash: Trash) {
        this.anims.create({
            key: config.name,
            frames: this.anims.generateFrameNumbers(config.name, { start: 0, end: 3 }),
            frameRate: 1,
            repeat: -1
        });
        trash.anims.play(config.name);
    }

    putTrashOnBoat(diver, boat) {
        var trash = this.currentTrash;
        this.currentTrash = null;
        this.counter--;
        this.counterText.setText(this.counter + "");

        trash.onBoat = true;
        trash.setVisible(false);
        // Check if the game is over
        this.isGameOver();

        // If they just got rid of the cans, delete the open mouth animation
        if(trash.name === "cans") {
            this.anims.get("openMouth").destroy();
        }
        // If the snugglefish is hidden, show it again
        if(this.snugglefish.x <= -100) {
            this.showSnugglefish();
        }
        this.fishy.anims.play("fishy");
    }

    isGameOver() {
        var trash: Trash;
        for (trash of (this.trash.getChildren() as Trash[])) {
            if (!trash.onBoat) {
                return false;
            }
        }

        this.endGame();

        return true;
    }

    update() {
        this.updatePlayerMovement();
    }

    updatePlayerMovement(): void {
        if (!this.diver.body.enable) {
            return;
        }

        this.diver.setAngularVelocity(0);
        this.diver.setVelocity(0);

        if (this.gamePad.keys.left.isDown) {
            this.diver.setAngularVelocity(-this.speed);
        }
        if (this.gamePad.keys.right.isDown) {
            this.diver.setAngularVelocity(this.speed);
        }
        if (this.gamePad.keys.up.isDown) {
            var vel = this.physics.velocityFromAngle(this.diver.angle + 90, this.speed);
            this.diver.setVelocity(vel.x, vel.y);
        }

        // Diver is carrying trash
        if (this.isCarryingTrash()) {
            this.currentTrash.x = this.diver.x + 60;
            this.currentTrash.y = this.diver.y;
        }

    }

    endGame() {
        this.diver.setVelocity(0, 0);
        this.diver.disableBody();
        this.input.enabled = false;

        setTimeout(() => {
            this.scene.add("EndGameScene", EndGameScene, true);
            this.scene.remove(this);
        }, 1000);       
    }

    hideSnugglefish(diver, snugglefishZone) {
        this.tweens.add({
            targets: this.snugglefish,
            x: -110,
            duration: 1000,
            ease: "Linear",
        });
    }

    showSnugglefish() {
        this.tweens.add({
            targets: this.snugglefish,
            x: 90,
            duration: 2500,
            ease: "Linear",
        });
    }

    openFishyMouth(diver, fishyzone) {
        if(this.fishy.anims.getCurrentKey() === "openMouth") {
            return;
        }

        this.fishy.anims.play('openMouth', true);
    }

}



// WEBPACK FOOTER //
// ./src/games/OceanCleanupGame/scenes/MainScene.ts